import { SpecificationUpdates } from '@/components/admin/spec-files/SpecificationUpdates';
import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import { Button } from '@/components/shared/Button';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useMemo,
  useState, useRef,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  v2,
  type SpecFile,
  type SpecFileDiffResult,
  type SpecFileWriteBody,
  type SpecFilesListResponse,
  type SpecSlotView,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { Spinner } from '@/components/dashboard/Spinner';
import { Pagination } from '@/components/dashboard/Pagination';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { PageToast } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
import { formatDate } from '@/lib/format';
import { PermissionDenied } from '@/pages/PermissionDenied';
import { SpecFileSummaryModal } from '@/components/admin/spec-files/SpecFileSummaryModal';
import { SpecFileContentModal } from '@/components/admin/spec-files/SpecFileContentModal';
import { SpecFileDiffModal } from '@/components/admin/SpecFileDiffModal';
import { SpecFileReviewPanel } from '@/components/admin/spec-files/SpecFileReviewPanel';
import { OfficialSpecLibrary } from '@/components/admin/spec-files/OfficialSpecLibrary';
import { specCellStyle as tdStyle, specFilterStyle, specSectionDescriptionStyle, specSectionHeadingStyle, specSelectStyle as selectStyle } from '@/components/admin/spec-files/specFileStyles';

const PAGE_SIZE = 50;

// phase-spec-files-stepper-seq §2 — Spec Files is a read-only reference list
// (like Validation Rules). The admin action controls (Upload / + New / View /
// Edit / Delete) are hidden from ALL users, including admins. The underlying
// create/update/delete handlers and modals are intentionally KEPT in the code
// (only the UI affordances are gated off) so the capability can be re-enabled
// by flipping this single flag — no backend routes or logic were removed.
const SHOW_SPEC_ADMIN_CONTROLS = false;

// Region values accepted by the Phase 5.14 backend (see src/routes/spec-files.ts
// VALID_REGIONS).
// 'all' intentionally excluded — the dropdown hardcodes a single <option value="All">
// sentinel already. Including 'all' here created a duplicate "All" entry and caused
// the region filter to send q.region='all' (≠ 'All') to the backend, returning 0 rows.
// 'Swissmedic' added to match VALID_REGIONS (backend) and the seeded CH spec catalog row.
const REGIONS = ['FDA', 'EMA', 'HC', 'PMDA', 'TGA', 'MHRA', 'Swissmedic'] as const;
// File-type vocabulary used by the V1 catalog (dtd/xsl/css/xml-schema/pdf/other).
// Backend allows any string ≤32 chars; this list seeds the dropdowns.
//
// phase-fix-spec-slot-match-and-docx-classify-bytes (Fix A) — this list mirrors
// the CANONICAL vocabulary defined once on the server in
// `src/lib/spec-files/fileTypes.ts` (`SPEC_FILE_TYPES`). An XML Schema is
// 'xml-schema'; the legacy 'xsd' spelling was normalised away by
// `20260924000000_normalize_specfiles_filetype_xsd` and is rejected from
// re-entering by the write seams, so stored values are always canonical and
// need no synonym handling here.
const FILE_TYPES = ['dtd', 'xsl', 'css', 'xml-schema', 'pdf', 'other'] as const;
const SHA256_HEX = /^[0-9a-fA-F]{64}$/;

interface FormDraft {
  Title: string;
  Region: string;
  Module: string;
  FileType: string;
  Version: string;
  Description: string;
  StoragePath: string;
  FileName: string;
  MimeType: string;
  Sha256: string;
  IsActive: boolean;
}

const EMPTY_DRAFT: FormDraft = {
  Title: '',
  Region: 'all',
  Module: '',
  FileType: 'dtd',
  Version: '',
  Description: '',
  StoragePath: '',
  FileName: '',
  MimeType: '',
  Sha256: '',
  IsActive: true,
};

function entryToDraft(s: SpecFile): FormDraft {
  return {
    Title: s.Title,
    Region: s.Region,
    Module: s.Module ?? '',
    FileType: s.FileType,
    Version: s.Version ?? '',
    Description: s.Description ?? '',
    StoragePath: s.StoragePath ?? '',
    FileName: s.FileName ?? '',
    MimeType: s.MimeType ?? '',
    Sha256: s.Sha256 ?? '',
    IsActive: s.IsActive,
  };
}

function draftToBody(draft: FormDraft): SpecFileWriteBody {
  const body: SpecFileWriteBody = {
    region: draft.Region,
    fileType: draft.FileType,
    title: draft.Title,
    isActive: draft.IsActive,
  };
  body.module = draft.Module === '' ? null : draft.Module;
  body.version = draft.Version === '' ? null : draft.Version;
  body.description = draft.Description === '' ? null : draft.Description;
  body.storagePath = draft.StoragePath === '' ? null : draft.StoragePath;
  body.fileName = draft.FileName === '' ? null : draft.FileName;
  body.mimeType = draft.MimeType === '' ? null : draft.MimeType;
  body.sha256 = draft.Sha256 === '' ? null : draft.Sha256;
  return body;
}

interface ModalState {
  readonly mode: 'create' | 'edit';
  readonly entry: SpecFile | null;
}

// Backend (src/routes/spec-files.ts) emits `{ error, message? }` for
// 400/404. Bare codes (NotFound, AuthRequired) get a friendlier rewrite
// via the map below; anything else falls through to parsed.message →
// parsed.error → err.message → fallback. Phase E-polish-extract hoisted
// the formatXxxError helper to `@/lib/errors`; this map is passed in
// as the `codeMap` argument.
const SPEC_FILE_ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This spec file no longer exists. The list may be out of date.',
  AuthRequired: 'Your session expired. Please sign in again.',
};

// Phase E-polish-extract-3 — inlined ConfirmModal was hoisted to
// `@/components/shared/ConfirmModal`. PageToast was hoisted in
// Phase E-polish-extract-2. parseApiErrorBody was hoisted in
// Phase E-polish-extract.


// TODO-075 / T15-D3 — canonical regulatory spec-file slots (V1 parity, gap #7
// from PHASE_audit_admin_screens). The slot list and each slot's status come
// from the SERVER (`slots` on the list response), resolved with the
// submission resolver's coverage rule — the page never re-derives them. A
// frontend copy matched on Region equality used to paint MHRA "NOT
// CONFIGURED" while MHRA submissions were pinned to the EU row.

export function SpecFilesPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('SpecFiles', 'read');
  const canWrite = perms.hasPermission('SpecFiles', 'write');
  const canDelete = perms.hasPermission('SpecFiles', 'delete');

  const [specFiles, setSpecFiles] = useState<SpecFile[] | null>(null);
  // T15-D3 — server-resolved slots; null = the server sent none (no claims).
  const [slots, setSlots] = useState<SpecSlotView[] | null>(null);
  // T15-D6 — the server's enforcement statement; null = none sent (no claim).
  const [enforcement, setEnforcement] = useState<SpecFilesListResponse['enforcement'] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [regionFilter, setRegionFilter] = useState<string>('All');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('All');
  // Default: show only active. Toggling the checkbox includes inactive rows.
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  // Sort state — server-side ORDER BY via sortBy/sortOrder query params.
  const [sortBy, setSortBy] = useState<string>('Region');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpecFile | null>(null);
  // Phase 5.X-v1-parity-polish Feature 2 — selected row whose summary
  // modal is open, or null when closed.
  const [summaryTarget, setSummaryTarget] = useState<SpecFile | null>(null);
  // phase-spec-files-management — content viewer, per-row busy state, and the
  // supersede-warning banner (notify, never auto-migrate).
  const [contentTarget, setContentTarget] = useState<SpecFile | null>(null);
  // phase-spec-enforcement-wiring — the upload dialog (set + source field).
  const [uploadDialog, setUploadDialog] = useState<{
    target: SpecFile;
    files: Array<{ fileName: string; content: string }>;
    primaryIdx: number;
    /** T15-D5 — required; the server builds the title from it. */
    versionLabel: string;
    sourceUrl: string;
    authorityVersionLabel: string;
  } | null>(null);
  const [fileBusyId, setFileBusyId] = useState<string | null>(null);
  // T15-D1 — the version under review; activation happens only from there.
  const [reviewTargetId, setReviewTargetId] = useState<string | null>(null);
  const [supersedeNotice, setSupersedeNotice] = useState<string | null>(null);
  const uploadTargetRef = useRef<SpecFile | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  // Phase spec-file-upload-diff (TODO-071) — staged upload + diff modal.
  // The publisher picks a DTD/XSD file, we call /preview-diff to compute
  // the section delta, then show <SpecFileDiffModal> as a gate before
  // committing the upload via the existing create endpoint.
  const [diffPending, setDiffPending] = useState<null | {
    fileName: string;
    fileContent: string;
    fileType: 'dtd' | 'xml-schema';
    region: string;
    currentSpecFileId: string | null;
    result: SpecFileDiffResult;
  }>(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const { toast, showToast, clearToast } = useUiToast();

  // Reset to page 1 when filters change.
  useEffect(() => setPage(1), [regionFilter, fileTypeFilter, showInactive]);

  // Column header click handler — clicking the active column flips direction;
  // clicking a new column resets to ascending.
  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  }, [sortBy]);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setSpecFiles(null);
    setSlots(null);
    setEnforcement(null);
    setError(null);
    const q: Parameters<typeof v2.specFiles.list>[0] = { page, pageSize: PAGE_SIZE };
    if (regionFilter !== 'All') q.region = regionFilter;
    if (fileTypeFilter !== 'All') q.fileType = fileTypeFilter;
    // phase-fix-spec-files-inactive-filter — the "Show inactive" toggle is now
    // a CLIENT-side filter (the payload is tiny — one page holds the whole
    // inventory at PAGE_SIZE 50 for ~14 rows), so we ALWAYS fetch the full set
    // (active + inactive) here and let the render decide what to show. This
    // gives an honest "X of Y" total (Y = the full inventory) and lets the
    // filter hide the client-rendered NOT-CONFIGURED placeholders too, which a
    // server `isActive` param never could. Previously `isActive=true` was sent
    // when unchecked, but the placeholders (rendered client-side) ignored it —
    // that is why the checkbox looked like a no-op.
    q.sortBy = sortBy;
    q.sortOrder = sortOrder;
    v2.specFiles
      .list(q)
      .then((res) => {
        if (cancelled) return;
        setSpecFiles(res.specFiles);
        setSlots(res.slots ?? null);
        setEnforcement(res.enforcement ?? null);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(formatApiError(err, 'Failed to load spec files', SPEC_FILE_ERROR_MESSAGES));
      });
    return () => { cancelled = true; };
  }, [canRead, page, regionFilter, fileTypeFilter, reloadKey, sortBy, sortOrder]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Active/default selection is independent of stored-byte availability.
  // A metadata-only default stays visible so its missing content is actionable.
  const specView = useMemo(() => {
    const rows = specFiles ?? [];
    const displayRows = showInactive ? rows : rows.filter((f) => f.IsActive === true);
    // Placeholders belong to the full inventory only, and only in the
    // unfiltered view (no region/file-type narrowing), matching prior behavior.
    const unfiltered = regionFilter === 'All' && fileTypeFilter === 'All';
    const slotRows = showInactive && unfiltered ? (slots ?? []) : [];
    const unfilledSlots = slotRows.filter((s) => s.status === 'unresolved');
    const coveredSlots = slotRows.filter((s) => s.status === 'covered');
    // Honest "X of Y SPEC FILES": both are DB spec-file counts. Y = every DB
    // row (active + inactive); X = the rows visible now. So the hidden inactive
    // remainder is always legible (e.g. "12 of 14"). NOT-CONFIGURED placeholders
    // are the ABSENCE of a spec file, not a spec file — they render (checked)
    // but are deliberately excluded from this count.
    const fullInventory = total;
    const visibleCount = displayRows.length;
    return { displayRows, unfilledSlots, coveredSlots, fullInventory, visibleCount };
  }, [specFiles, slots, showInactive, regionFilter, fileTypeFilter, total]);

  // Phase spec-file-upload-diff (TODO-071) — user picked a file. Read it,
  // call /preview-diff (no current spec file yet because we don't have a
  // way to fetch existing bytes from the page), then stage <SpecFileDiffModal>.
  const handleFileSelected = useCallback(async (file: File): Promise<void> => {
    const lower = file.name.toLowerCase();
    const fileType: 'dtd' | 'xml-schema' = lower.endsWith('.xsd') ? 'xml-schema' : 'dtd';
    setDiffError(null);
    try {
      const fileContent = await file.text();
      // Look up the active spec file for the same region to populate
      // currentSpecFileId + currentVersion in the diff response. The region
      // is the current filter — when 'All' is selected, we send no region.
      let currentSpecFileId: string | null = null;
      if (regionFilter !== 'All') {
        try {
          const existing = await v2.specFiles.list({
            region: regionFilter,
            fileType,
            isActive: true,
            pageSize: 1,
          });
          if (existing.specFiles[0]) currentSpecFileId = existing.specFiles[0].Id;
        } catch {
          // Best-effort — diff still works without it (no removed sections).
        }
      }
      const result = await v2.specFiles.previewDiff({
        fileName: file.name,
        fileType,
        fileContent,
        currentSpecFileId,
      });
      setDiffPending({
        fileName: file.name,
        fileContent,
        fileType,
        region: regionFilter !== 'All' ? regionFilter : 'FDA',
        currentSpecFileId,
        result,
      });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to preview spec file diff', SPEC_FILE_ERROR_MESSAGES),
      });
    }
  }, [regionFilter, showToast]);

  // Step 2 — publisher confirmed. Persist metadata via the existing create
  // endpoint, refresh, close the modal.
  const handleDiffConfirmed = useCallback(async (): Promise<void> => {
    if (!diffPending) return;
    setDiffBusy(true);
    setDiffError(null);
    try {
      const body: SpecFileWriteBody = {
        region: diffPending.region,
        fileType: diffPending.fileType,
        title: diffPending.fileName.replace(/\.(dtd|xsd)$/i, '') || diffPending.fileName,
        fileName: diffPending.fileName,
        version: diffPending.result.newVersion || null,
        isActive: true,
      };
      await v2.specFiles.create(body);
      const fileName = diffPending.fileName;
      setDiffPending(null);
      reload();
      showToast({ type: 'success', text: `Uploaded "${fileName}".` });
    } catch (err) {
      setDiffError(formatApiError(err, 'Upload failed', SPEC_FILE_ERROR_MESSAGES));
    } finally {
      setDiffBusy(false);
    }
  }, [diffPending, reload, showToast]);

  // Phase D-2-polish: window.confirm() / window.alert() replaced with an
  // in-page <ConfirmModal> + toast. setDeleteTarget(s) opens the modal;
  // handleDeleteConfirmed runs the actual delete after the user confirms.
  // phase-spec-files-management — upload a replacement version (retained as a
  // NEW row, arriving INACTIVE; activation is a separate explicit step).
  // phase-spec-enforcement-wiring — picking files opens the upload dialog
  // (primary + companion modules + the ENCOURAGED-not-blocking source field,
  // Item 4). The dialog submits; nothing uploads on file-pick alone.
  const openUploadDialog = async (target: SpecFile, files: readonly File[]): Promise<void> => {
    const read = await Promise.all(
      files.map(async (f) => ({ fileName: f.name, content: await f.text() })),
    );
    // Primary = the file whose extension matches the slot's type, else first.
    const extFor = (ft: string): string =>
      ft === 'dtd' ? '.dtd' : ft === 'xml-schema' ? '.xsd' : ft === 'xsl' ? '.xsl' : '';
    const wantExt = extFor(target.FileType);
    const primaryIdx = Math.max(
      0,
      read.findIndex((f) => wantExt !== '' && f.fileName.toLowerCase().endsWith(wantExt)),
    );
    setUploadDialog({
      target,
      files: read,
      primaryIdx,
      versionLabel: '',
      sourceUrl: '',
      authorityVersionLabel: '',
    });
  };

  const handleUploadConfirm = async (): Promise<void> => {
    if (!uploadDialog) return;
    const { target, files, primaryIdx, versionLabel, sourceUrl, authorityVersionLabel } =
      uploadDialog;
    const primary = files[primaryIdx]!;
    const companions = files.filter((_, i) => i !== primaryIdx);
    setFileBusyId(target.Id);
    setSupersedeNotice(null);
    try {
      const res = await v2.specFiles.uploadVersion(target.Id, {
        fileName: primary.fileName,
        content: primary.content,
        versionLabel: versionLabel.trim(),
        ...(companions.length > 0 ? { companions } : {}),
        ...(sourceUrl.trim() !== '' ? { sourceUrl: sourceUrl.trim() } : {}),
        ...(authorityVersionLabel.trim() !== ''
          ? { authorityVersionLabel: authorityVersionLabel.trim() }
          : {}),
      });
      const setNote =
        companions.length > 0 ? ` (${files.length}-file set: ${companions.length} companion module${companions.length === 1 ? '' : 's'})` : '';
      showToast({
        type: 'success',
        text: `Uploaded "${res.title}" (${primary.fileName})${setNote} as a new INACTIVE version. Activate it explicitly to put it in force.`,
      });
      if (res.warning) setSupersedeNotice(res.warning);
      setUploadDialog(null);
      await reload();
      // T15-D1 — straight into the review: what activating it would change.
      setReviewTargetId(res.id);
    } catch (err) {
      showToast({ type: 'error', text: formatApiError(err, 'Upload failed.') });
    } finally {
      setFileBusyId(null);
    }
  };

  const handleSetActive = async (target: SpecFile, isActive: boolean): Promise<void> => {
    setFileBusyId(target.Id);
    setSupersedeNotice(null);
    try {
      const res = await v2.specFiles.setActive(target.Id, isActive);
      const deactivatedNote =
        res.deactivated.length > 0 ? ` Deactivated (retained): ${res.deactivated.join(', ')}.` : '';
      showToast({
        type: 'success',
        text: `${target.Title} is now ${isActive ? 'ACTIVE' : 'inactive'}.${deactivatedNote}`,
      });
      if (res.warning) setSupersedeNotice(res.warning);
      await reload();
    } catch (err) {
      showToast({ type: 'error', text: formatApiError(err, 'Could not switch active state.') });
    } finally {
      setFileBusyId(null);
    }
  };

  const handleDeleteConfirmed = async (s: SpecFile): Promise<void> => {
    setDeleteTarget(null);
    try {
      await v2.specFiles.delete(s.Id);
      reload();
      showToast({ type: 'success', text: `Deleted spec file "${s.Title}".` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to delete spec file', SPEC_FILE_ERROR_MESSAGES),
      });
    }
  };

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (perms.failed) return (
    <section role="alert" style={{ padding: 20 }}>
      <h1 className="screen-title">Specification Files</h1>
      <p>Permissions could not be checked. Try again when the service is available.</p>
      <Button variant="ghost" size="sm" onClick={() => void perms.refresh()}>Retry permissions</Button>
    </section>
  );
  if (!canRead) return <PermissionDenied />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          {/* phase-v1-ui-parity — V1 SpecFilesScreen: "Specification Files"
              title + the V1 subtitle copy, via .screen-title/.screen-subtitle. */}
          <h1 className="screen-title" style={{ margin: 0 }}>Specification Files</h1>
          <div className="screen-subtitle" style={{ marginBottom: 0 }}>
            Browse official reference files and the specifications configured for validation.
          </div>
        </div>
        {SHOW_SPEC_ADMIN_CONTROLS && canWrite && (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Phase spec-file-upload-diff (TODO-071) — file picker that
                routes through the diff modal BEFORE persisting. The hidden
                <input type="file"> is wired to a visible button via
                programmatic click; on change we hand the File to
                handleFileSelected. Accepts .dtd and .xsd. */}
            <label
              data-testid="spec-file-upload-button"
              style={{
                background: 'var(--surface)',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Upload spec file
              <input
                type="file"
                accept=".dtd,.xsd,application/xml-dtd,application/xml,text/xml"
                data-testid="spec-file-upload-input"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Reset the input so re-picking the same file fires onChange.
                  e.target.value = '';
                  if (file) void handleFileSelected(file);
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => setModal({ mode: 'create', entry: null })}
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + New spec file
            </button>
          </div>
        )}
      </div>

      <SpecificationUpdates key={`${perms.email ?? ''}:${canWrite}`} canWrite={canWrite} />
      <OfficialSpecLibrary />
      <h2 style={specSectionHeadingStyle}>Configured validation files</h2>
      <p data-testid="spec-files-active-meaning" style={specSectionDescriptionStyle}>
        Regional defaults and shared reference files.
      </p>
      {/* Filter row */}
      <div style={specFilterStyle}>
        <label style={{ fontSize: 12 }}>
          Region
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            aria-label="Region filter"
            style={selectStyle}
          >
            <option value="All">All</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          File type
          <select
            value={fileTypeFilter}
            onChange={(e) => setFileTypeFilter(e.target.value)}
            aria-label="File type filter"
            style={selectStyle}
          >
            <option value="All">All</option>
            {FILE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            aria-label="Show inactive"
          />
          Show inactive
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {specFiles !== null && `${specView.visibleCount} of ${specView.fullInventory} spec file${
            specView.fullInventory === 1 ? '' : 's'
          }`}
        </span>
      </div>

      {/* T15-D6 — the enforcement statement is SERVER wording
          (src/lib/spec-files/enforcementWording.ts), the same source every
          upload / activation notice uses, so the two can never contradict
          each other again (the notice used to say "not yet used during
          validation" under a banner saying "Enforced"). The content gap —
          active rows with no stored file, which validation SKIPS — is
          stated alongside. No statement from the server → no claim. */}
      {enforcement && (
        <details
          data-testid="spec-files-enforced-note"
          style={{
            margin: '0 0 14px 0',
            padding: '10px 12px',
            borderRadius: 6,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            color: 'var(--text)',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <summary style={{ cursor: 'pointer' }}>Validation details</summary>
          <p style={{ margin: '8px 0 0' }}>{enforcement.statement}</p>
          {enforcement.contentGap !== null && (
            <div
              data-testid="spec-files-content-gap"
              style={{ marginTop: 6, color: 'var(--amber)', fontWeight: 600 }}
            >
              {enforcement.contentGap}
            </div>
          )}
        </details>
      )}

      {/* phase-spec-files-management — supersede warning: notify, never
          auto-migrate. */}
      {supersedeNotice !== null && (
        <div
          role="alert"
          data-testid="spec-file-supersede-warning"
          style={{ margin: '0 0 12px 0', padding: '10px 12px', borderRadius: 6, background: '#fef3c7', border: '1px solid #fcd34d', color: '#78350f', fontSize: 13 }}
        >
          <strong>Heads up:</strong> {supersedeNotice}
          <button
            type="button"
            onClick={() => setSupersedeNotice(null)}
            aria-label="Dismiss warning"
            style={{ marginLeft: 10, background: 'transparent', border: 'none', cursor: 'pointer', color: '#78350f', fontWeight: 700 }}
          >
            ×
          </button>
        </div>
      )}
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        accept=".dtd,.xsd,.xsl,.css,.xml,.mod,.txt"
        style={{ display: 'none' }}
        data-testid="spec-file-upload-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          const target = uploadTargetRef.current;
          e.target.value = '';
          if (files.length > 0 && target) void openUploadDialog(target, files);
        }}
      />
      {uploadDialog !== null && (
        <MovableDialog
          persistKey="spec-file-upload"
          title={`Upload version — ${uploadDialog.target.Title}`}
          onClose={() => setUploadDialog(null)}
          testId="spec-file-upload-dialog"
          width={560}
        >
          <div style={{ fontSize: 13, display: 'grid', gap: 10 }}>
            <div>
              {uploadDialog.files.length === 1 ? (
                <>
                  File: <strong>{uploadDialog.files[0]!.fileName}</strong>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 6 }}>
                    {uploadDialog.files.length}-file specification set — pick the primary file
                    (the others register as companion modules, resolved by name):
                  </div>
                  {uploadDialog.files.map((f, i) => (
                    <label key={f.fileName} style={{ display: 'block', marginBottom: 2 }}>
                      <input
                        type="radio"
                        name="spec-upload-primary"
                        checked={uploadDialog.primaryIdx === i}
                        onChange={() => setUploadDialog({ ...uploadDialog, primaryIdx: i })}
                      />{' '}
                      {f.fileName}
                      {uploadDialog.primaryIdx === i ? ' (primary)' : ''}
                    </label>
                  ))}
                </>
              )}
            </div>
            {/* T15-D5 — required, unique within the slot; the server builds
                the new version's title from it ("<family> <label>"). */}
            <label style={{ display: 'grid', gap: 4 }}>
              <span>
                Version label — <strong>required</strong>; unique for this specification (e.g. 3.4)
              </span>
              <input
                type="text"
                data-testid="spec-file-upload-version-label"
                value={uploadDialog.versionLabel}
                maxLength={32}
                onChange={(e) => setUploadDialog({ ...uploadDialog, versionLabel: e.target.value })}
                placeholder="e.g. 3.4"
                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span>
                Source (URL or statement) — <em>encouraged; where this file came from</em>
              </span>
              <input
                type="text"
                data-testid="spec-file-upload-source"
                value={uploadDialog.sourceUrl}
                onChange={(e) => setUploadDialog({ ...uploadDialog, sourceUrl: e.target.value })}
                placeholder="e.g. https://esubmission.ema.europa.eu/… — left empty, the row shows “source not recorded”"
                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span>Authority&rsquo;s version label (as published) — optional</span>
              <input
                type="text"
                data-testid="spec-file-upload-authority-version"
                value={uploadDialog.authorityVersionLabel}
                onChange={(e) =>
                  setUploadDialog({ ...uploadDialog, authorityVersionLabel: e.target.value })
                }
                placeholder="e.g. EU Regional DTD 3.1 (EU M1 spec v3.1.1)"
                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setUploadDialog(null)}
                style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="spec-file-upload-confirm"
                disabled={fileBusyId !== null || uploadDialog.versionLabel.trim() === ''}
                onClick={() => void handleUploadConfirm()}
                style={{ padding: '6px 14px', border: '1px solid var(--accent)', borderRadius: 6, background: 'var(--accent)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                Upload (arrives inactive)
              </button>
            </div>
          </div>
        </MovableDialog>
      )}
      {contentTarget !== null && (
        <SpecFileContentModal specFile={contentTarget} onClose={() => setContentTarget(null)} />
      )}
      {reviewTargetId !== null && (
        <SpecFileReviewPanel
          specFileId={reviewTargetId}
          onClose={() => setReviewTargetId(null)}
          onActivated={({ title, deactivated, warning }) => {
            setReviewTargetId(null);
            showToast({
              type: 'success',
              text: `${title} is now ACTIVE.${
                deactivated.length > 0 ? ` Deactivated (retained): ${deactivated.join(', ')}.` : ''
              }`,
            });
            setSupersedeNotice(warning);
            reload();
          }}
        />
      )}

      {specFiles === null && !error && <Spinner caption="Loading spec files…" />}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
          <Button
            variant="ghost"
            size="sm"
            onClick={reload}
            style={{ marginLeft: 12 }}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Phase 5.13f — always render the table with column headers.
          Server-side filters → zero-loaded vs filter-excluded is
          inferred from whether any filter is set to a non-default
          value (regionFilter !== 'All', fileTypeFilter !== 'All', or
          showInactive=true which excludes inactive rows). Column count
          is 7 when the Actions column renders (canWrite || canDelete),
          6 otherwise. */}
      {specFiles !== null && (
        <>
          {(() => {
            // phase-spec-files-stepper-seq §5 — SHA-256 and Sections columns
            // were dropped (always empty). Remaining columns: Title, Region,
            // File type, Version, Active, Uploaded by, Uploaded at, Updated (8),
            // plus the Actions column only when admin controls are shown (9).
            const colSpan = SHOW_SPEC_ADMIN_CONTROLS ? 10 : 9;
            // phase-fix-spec-files-inactive-filter — the "Show inactive" filter
            // is computed once in `specView` (above) so the count span and the
            // table agree. UNCHECKED → usable rows only; CHECKED → the full
            // inventory (inactive DB rows + NOT-CONFIGURED placeholders).
            const { displayRows, unfilledSlots, coveredSlots } = specView;
            // "Genuinely empty" (→ "No spec files yet") only when the DB itself
            // is empty AND no Region/File-type filter is narrowing the view. The
            // backend computes `total` AFTER the region/type filter, so a filter
            // that excludes everything also yields total 0 — that case is
            // "no match", not "no data".
            const genuinelyEmpty =
              total === 0 && regionFilter === 'All' && fileTypeFilter === 'All';
            return (
              <ResponsiveTable
                aria-label="Configured validation files"
                data-testid="spec-files-table"
                style={{ width: '100%', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                    {(['Title', 'Region', 'FileType', 'Version'] as const).map((col) => {
                      const label = col === 'FileType' ? 'File type' : col;
                      const width = { Title: '25%', Region: '8%', FileType: '8%', Version: '8%' }[col];
                      return (
                        <th
                          key={col}
                          style={{ ...tdStyle, width, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort(col)}
                          aria-sort={sortBy === col ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                          data-testid={`sort-header-${col}`}
                        >
                          {label}{sortBy === col ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                        </th>
                      );
                    })}
                    <th style={{ ...tdStyle, width: '12%', textAlign: 'center' }}>Default / reference</th>
                    {/* TODO-075 — V1 parity: "Uploaded by" surfaces CreatedBy. */}
                    <th
                      style={{ ...tdStyle, width: '10%', whiteSpace: 'nowrap' }}
                      data-testid="spec-files-header-uploaded-by"
                    >
                      Uploaded by
                    </th>
                    {/* TODO-075 — V1 parity: "Uploaded at" surfaces CreatedAt. */}
                    <th
                      style={{ ...tdStyle, width: '10%', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('CreatedAt')}
                      aria-sort={sortBy === 'CreatedAt' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                      data-testid="sort-header-CreatedAt"
                    >
                      Uploaded at{sortBy === 'CreatedAt' ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th
                      style={{ ...tdStyle, width: '8%', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('UpdatedAt')}
                      aria-sort={sortBy === 'UpdatedAt' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                      data-testid="sort-header-UpdatedAt"
                    >
                      Updated{sortBy === 'UpdatedAt' ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    {/* phase-spec-files-management — the FILE actions column
                        (view / download / upload / activate) is ALWAYS
                        rendered; Sharon's 2026-08-02 ruling supersedes the
                        stepper-seq read-only gating for file management. The
                        legacy metadata-CRUD column stays behind the flag. */}
                    <th style={{ ...tdStyle, width: '11%', whiteSpace: 'nowrap' }}>File</th>
                    {SHOW_SPEC_ADMIN_CONTROLS && <th style={tdStyle}></th>}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.length === 0 && unfilledSlots.length === 0 ? (
                    <tr data-testid="spec-files-empty-row">
                      <td
                        colSpan={colSpan}
                        style={{
                          padding: '40px 16px',
                          textAlign: 'center',
                          color: 'var(--text-muted)',
                          fontSize: 13,
                        }}
                      >
                        {genuinelyEmpty ? (
                          <>
                            <div
                              style={{ fontSize: 32, marginBottom: 8 }}
                              aria-hidden="true"
                            >
                              📄
                            </div>
                            <div
                              style={{
                                fontWeight: 600,
                                color: 'var(--text)',
                                marginBottom: 4,
                              }}
                            >
                              No spec files yet
                            </div>
                            <div>
                              Spec files are region-scoped DTD / XSL / schema
                              assets used during package validation.
                              {SHOW_SPEC_ADMIN_CONTROLS && canWrite && ' Create one to register a new spec.'}
                            </div>
                            {SHOW_SPEC_ADMIN_CONTROLS && canWrite && (
                              <button
                                type="button"
                                onClick={() => setModal({ mode: 'create', entry: null })}
                                style={{
                                  marginTop: 12,
                                  padding: '6px 12px',
                                  border: '1px solid var(--accent)',
                                  borderRadius: 6,
                                  background: 'var(--accent)',
                                  color: '#fff',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                + Create first spec file
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <div
                              style={{
                                fontWeight: 600,
                                color: 'var(--text)',
                                marginBottom: 4,
                              }}
                            >
                              No spec files match the current filters
                            </div>
                            <div>
                              Showing 0 of {specView.fullInventory} spec file
                              {specView.fullInventory === 1 ? '' : 's'}. Reset the
                              Region or File-type filter — or enable “Show
                              inactive” — to see all spec files.
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ) : (
                    displayRows.map((s) => (
                <tr
                  key={s.Id}
                  style={{ borderBottom: '1px solid var(--border)' }}
                  data-testid={`spec-file-row-${s.Id}`}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>
                      {s.Title}
                      {(s.CompanionCount ?? 0) > 0 && (
                        <span
                          data-testid={`spec-file-set-badge-${s.Id}`}
                          title={`This specification is a ${(s.CompanionCount ?? 0) + 1}-file set: the primary file plus ${s.CompanionCount} companion module${s.CompanionCount === 1 ? '' : 's'}, versioned and activated together.`}
                          style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: 'var(--accent-soft, #e0e7ff)', color: 'var(--accent)', whiteSpace: 'nowrap' }}
                        >
                          {(s.CompanionCount ?? 0) + 1}-file set
                        </span>
                      )}
                      {s.AlsoAppliesToRegions && (
                        <span
                          data-testid={`spec-file-shared-badge-${s.Id}`}
                          title={`Shared entry: also serves ${s.AlsoAppliesToRegions} (the authority publishes one file for both).`}
                          style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: '#fef9c3', color: '#854d0e', whiteSpace: 'nowrap' }}
                        >
                          also {s.AlsoAppliesToRegions}
                        </span>
                      )}
                    </div>
                    {/* phase-spec-enforcement-wiring Item 4 — provenance on the
                        row: source (linked when a URL), fetch date, the
                        authority's own version label, checksum. An empty
                        source is an HONEST GAP: "source not recorded". */}
                    <div
                      data-testid={`spec-file-provenance-${s.Id}`}
                      style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}
                    >
                      {s.SourceUrl ? (
                        /^https?:\/\//i.test(s.SourceUrl) ? (
                          <a href={s.SourceUrl} target="_blank" rel="noreferrer" title={s.SourceUrl}>
                            source
                          </a>
                        ) : (
                          <span title={s.SourceUrl}>source: {s.SourceUrl.slice(0, 40)}{s.SourceUrl.length > 40 ? '…' : ''}</span>
                        )
                      ) : (
                        <span style={{ fontStyle: 'italic' }}>source not recorded</span>
                      )}
                      {s.SourceFetchedAt ? ` · fetched ${formatDate(s.SourceFetchedAt)}` : ''}
                      {s.AuthorityVersionLabel ? ` · ${s.AuthorityVersionLabel}` : ''}
                      {s.Sha256 ? (
                        <span title={`SHA-256: ${s.Sha256}`}> · sha256 {s.Sha256.slice(0, 12)}…</span>
                      ) : (
                        ''
                      )}
                    </div>
                    {s.Description && (
                      <details style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        <summary>Description</summary>
                        <p style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{s.Description}</p>
                      </details>
                    )}
                    <div data-testid={`spec-file-availability-${s.Id}`} style={{ marginTop: 7, fontSize: 12 }}>
                      <strong>File availability: </strong>
                      {s.ContentSource === 'embedded'
                        ? 'Available — embedded copy'
                        : s.ContentSource === 'stored' || (s.ContentSource === undefined && (s.HasContent === true || s.HasContent === 1))
                          ? 'Available — stored content'
                          : s.ContentSource === 'unavailable' || (s.ContentSource === undefined && (s.HasContent === false || s.HasContent === 0))
                            ? 'Unavailable — metadata only'
                            : 'Unknown — the server did not report availability'}
                    </div>
                    <details data-testid={`spec-file-support-${s.Id}`} style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                      <summary>AutoeCTD support</summary><p style={{ margin: '6px 0 0' }}>{s.Support?.message ?? 'Support status not reported.'}</p>
                    </details>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13 }}>{s.Region}</td>
                  <td
                    style={{
                      ...tdStyle,
                      fontFamily: 'monospace',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                    }}
                  >
                    {s.FileType}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontFamily: 'monospace',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                    }}
                  >
                    {s.Version ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontSize: 12 }}>
                    {s.IsActive ? (
                      <span aria-label="Active">
                        {s.Region === 'all' ? 'Common reference' : 'Default for new applications'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }} aria-label="Inactive">
                        Inactive
                      </span>
                    )}
                  </td>
                  {/* TODO-075 — Uploaded by surfaces CreatedBy. */}
                  <td
                    style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}
                    data-testid={`spec-file-uploaded-by-${s.Id}`}
                    title={s.CreatedBy}
                  >
                    {s.CreatedBy}
                  </td>
                  {/* TODO-075 — Uploaded at surfaces CreatedAt. */}
                  <td
                    style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}
                    data-testid={`spec-file-uploaded-at-${s.Id}`}
                  >
                    {formatDate(s.CreatedAt)}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                    {formatDate(s.UpdatedAt ?? s.CreatedAt)}
                  </td>
                  {/* phase-spec-files-management — file actions. */}
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`spec-file-view-content-${s.Id}`}
                        onClick={() => setContentTarget(s)}
                      >
                        View
                      </Button>
                      {s.SpecificationUpdateId && <span style={{ fontSize: 12, whiteSpace: 'normal', color: 'var(--text-muted)' }}>Managed in Specification updates</span>}
                      {canWrite && !s.SpecificationUpdateId && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`spec-file-upload-version-${s.Id}`}
                            disabled={fileBusyId === s.Id}
                            onClick={() => {
                              uploadTargetRef.current = s;
                              uploadInputRef.current?.click();
                            }}
                            title="Upload a replacement version (retained as a new inactive row)"
                          >
                            Upload…
                          </Button>
                          {s.IsActive ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`spec-file-toggle-active-${s.Id}`}
                              disabled={fileBusyId === s.Id}
                              onClick={() => void handleSetActive(s, false)}
                              title="Deactivate (retained, never deleted). Affects the default for NEW applications only — existing applications keep their pinned specification."
                            >
                              Deactivate
                            </Button>
                          ) : (
                            // T15-D1 — no one-click activation: review the
                            // change against the version in force first.
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`spec-file-review-${s.Id}`}
                              disabled={fileBusyId === s.Id}
                              onClick={() => setReviewTargetId(s.Id)}
                              title="Review what activating this version changes, then activate it from the review."
                            >
                              Review…
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  {/* phase-spec-files-stepper-seq §2 — the Actions column
                      (View / Edit / Delete) is hidden from ALL users; Spec
                      Files is a read-only reference list. Gated behind
                      SHOW_SPEC_ADMIN_CONTROLS so the handlers stay wired up
                      and the capability can be re-enabled by flipping it. */}
                  {SHOW_SPEC_ADMIN_CONTROLS && (
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => setSummaryTarget(s)}
                      data-testid={`spec-file-view-summary-btn-${s.Id}`}
                      style={{
                        background: 'none',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        padding: '4px 10px',
                        marginRight: 8,
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      View
                    </button>
                    {canWrite && !s.SpecificationUpdateId && (
                      <button
                        type="button"
                        onClick={() => setModal({ mode: 'edit', entry: s })}
                        style={{
                          background: 'none',
                          color: 'var(--accent)',
                          border: '1px solid var(--accent)',
                          borderRadius: 4,
                          padding: '4px 10px',
                          marginRight: 8,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && !s.SpecificationUpdateId && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(s)}
                        data-testid={`spec-file-delete-btn-${s.Id}`}
                        style={{
                          background: 'none',
                          color: 'var(--red)',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                  )}
                    </tr>
                    ))
                  )}
                  {/* T15-D3 — a slot covered by another region's row (MHRA by
                      the EU row). Says what serves it; not interactive. */}
                  {coveredSlots.map((slot) => (
                    <tr
                      key={`covered-${slot.region}-${slot.fileType}`}
                      style={{ borderBottom: '1px solid var(--border)' }}
                      data-testid={`spec-file-covered-row-${slot.region}-${slot.fileType}`}
                    >
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 500 }}>{`${slot.region} ${slot.fileType}`}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {slot.message}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 13 }}>{slot.region}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                        {slot.fileType}
                      </td>
                      <td style={tdStyle}>{slot.spec?.Version ?? '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>—</td>
                      <td style={tdStyle}>—</td>
                      <td style={tdStyle}>—</td>
                      <td style={tdStyle}>—</td>
                      {SHOW_SPEC_ADMIN_CONTROLS && <td style={tdStyle} />}
                    </tr>
                  ))}
                  {/* TODO-075 — placeholder rows for slots that resolve to
                      NOTHING (T15-D3: per the server). Shown only when no
                      filter is active (otherwise they would conflict with the
                      filtered view). Not interactive; no action buttons. */}
                  {unfilledSlots.map((slot) => (
                    <tr
                      key={`placeholder-${slot.region}-${slot.fileType}`}
                      style={{ borderBottom: '1px solid var(--border)', opacity: 0.55 }}
                      data-testid={`spec-file-placeholder-row-${slot.region}-${slot.fileType}`}
                    >
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 500, color: 'var(--text-muted)' }}>
                          {`${slot.region} ${slot.fileType}`}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {slot.message}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginTop: 2,
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '1px 6px',
                              border: '1px solid var(--border)',
                              borderRadius: 4,
                              fontSize: 10,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                            }}
                            data-testid={`spec-file-placeholder-badge-${slot.region}-${slot.fileType}`}
                          >
                            Not configured
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                        {slot.region}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          fontFamily: 'monospace',
                          fontSize: 12,
                          color: 'var(--text-muted)',
                        }}
                      >
                        {slot.fileType}
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>—</td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                        —
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>—</td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>—</td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>—</td>
                      {SHOW_SPEC_ADMIN_CONTROLS && (
                        <td style={tdStyle}>{/* no actions on placeholder */}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            );
          })()}
          {specFiles.length > 0 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      {modal && (
        <SpecFileEditModal
          mode={modal.mode}
          entry={modal.entry}
          onClose={() => setModal(null)}
          onSaved={(savedTitle) => {
            const wasCreate = modal.mode === 'create';
            setModal(null);
            reload();
            showToast({
              type: 'success',
              text: wasCreate
                ? `Created spec file "${savedTitle}".`
                : `Saved changes to "${savedTitle}".`,
            });
          }}
        />
      )}

      {summaryTarget && (
        <SpecFileSummaryModal
          entry={summaryTarget}
          onClose={() => setSummaryTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete spec file"
          message={`Permanently delete "${deleteTarget.Title}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}

      {/* Phase spec-file-upload-diff (TODO-071) — diff preview gate. */}
      {diffPending && (
        <SpecFileDiffModal
          fileName={diffPending.fileName}
          result={diffPending.result}
          busy={diffBusy}
          errorMessage={diffError}
          onCancel={() => {
            if (!diffBusy) {
              setDiffPending(null);
              setDiffError(null);
            }
          }}
          onConfirm={() => void handleDiffConfirmed()}
        />
      )}

      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}

interface ModalProps {
  readonly mode: 'create' | 'edit';
  readonly entry: SpecFile | null;
  readonly onClose: () => void;
  /** Phase D-2-polish: callback now receives the saved title so the page
   *  can show a toast like `Created spec file "X"`. */
  readonly onSaved: (savedTitle: string) => void;
}

function SpecFileEditModal({ mode, entry, onClose, onSaved }: ModalProps): ReactElement {
  const [draft, setDraft] = useState<FormDraft>(() =>
    entry ? entryToDraft(entry) : EMPTY_DRAFT,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof FormDraft, v: string | boolean): void =>
    setDraft((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!draft.Title.trim()) {
      setError('Title is required');
      return;
    }
    if (draft.Title.length > 200) {
      setError('Title must be ≤ 200 characters');
      return;
    }
    if (!draft.FileType.trim()) {
      setError('File type is required');
      return;
    }
    if (draft.FileType.length > 32) {
      setError('File type must be ≤ 32 characters');
      return;
    }
    if (draft.Module && draft.Module.length > 8) {
      setError('Module must be ≤ 8 characters');
      return;
    }
    if (draft.Version && draft.Version.length > 32) {
      setError('Version must be ≤ 32 characters');
      return;
    }
    if (draft.StoragePath && draft.StoragePath.length > 500) {
      setError('Storage path must be ≤ 500 characters');
      return;
    }
    if (draft.FileName && draft.FileName.length > 255) {
      setError('File name must be ≤ 255 characters');
      return;
    }
    if (draft.MimeType && draft.MimeType.length > 100) {
      setError('MIME type must be ≤ 100 characters');
      return;
    }
    if (draft.Sha256 && !SHA256_HEX.test(draft.Sha256)) {
      setError('SHA-256 must be exactly 64 hex characters');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const body = draftToBody(draft);
      if (mode === 'create') {
        await v2.specFiles.create(body);
      } else if (entry) {
        await v2.specFiles.update(entry.Id, body);
      }
      onSaved(draft.Title);
    } catch (err) {
      setError(formatApiError(err, 'Save failed', SPEC_FILE_ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: 13,
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="specFiles.editor"
      title={mode === 'create' ? 'New spec file' : `Edit "${entry?.Title}"`}
      ariaLabel={mode === 'create' ? 'New spec file' : 'Edit spec file'}
      onClose={onClose}
      closeOnBackdrop
      width="min(720px, 92vw)"
      maxHeight="88vh"
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <form onSubmit={handleSubmit}>


        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <Field label="Title *">
          <input
            type="text"
            value={draft.Title}
            onChange={(e) => update('Title', e.target.value)}
            required
            aria-label="Spec file title"
            style={inputStyle}
            maxLength={200}
          />
        </Field>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <Field label="Region *">
            <select
              value={draft.Region}
              onChange={(e) => update('Region', e.target.value)}
              aria-label="Spec file region"
              required
              style={inputStyle}
            >
              {REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
          <Field label="File type *">
            <select
              value={draft.FileType}
              onChange={(e) => update('FileType', e.target.value)}
              aria-label="Spec file type"
              required
              style={inputStyle}
            >
              {FILE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Module">
            <input
              type="text"
              value={draft.Module}
              onChange={(e) => update('Module', e.target.value)}
              placeholder="e.g. 1.1.1"
              maxLength={8}
              aria-label="Spec file module"
              style={inputStyle}
            />
          </Field>
          <Field label="Version">
            <input
              type="text"
              value={draft.Version}
              onChange={(e) => update('Version', e.target.value)}
              placeholder="e.g. 3.3"
              maxLength={32}
              aria-label="Spec file version"
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={draft.Description}
            onChange={(e) => update('Description', e.target.value)}
            aria-label="Spec file description"
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical',
            }}
          />
        </Field>

        <Field label="Storage path">
          <input
            type="text"
            value={draft.StoragePath}
            onChange={(e) => update('StoragePath', e.target.value)}
            placeholder="azure://… or empty for metadata-only"
            maxLength={500}
            aria-label="Spec file storage path"
            style={inputStyle}
          />
        </Field>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <Field label="File name">
            <input
              type="text"
              value={draft.FileName}
              onChange={(e) => update('FileName', e.target.value)}
              maxLength={255}
              aria-label="Spec file name"
              style={inputStyle}
            />
          </Field>
          <Field label="MIME type">
            <input
              type="text"
              value={draft.MimeType}
              onChange={(e) => update('MimeType', e.target.value)}
              placeholder="application/xml-dtd"
              maxLength={100}
              aria-label="Spec file mime type"
              style={inputStyle}
            />
          </Field>
        </div>

        <Field label="SHA-256 (64 hex chars)">
          <input
            type="text"
            value={draft.Sha256}
            onChange={(e) => update('Sha256', e.target.value)}
            placeholder="Optional; leave blank if no binary yet"
            aria-label="Spec file sha256"
            style={{
              ...inputStyle,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12,
            }}
            maxLength={64}
          />
        </Field>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={draft.IsActive}
            onChange={(e) => update('IsActive', e.target.checked)}
            aria-label="Spec file is active"
          />
          Active
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving
              ? 'Saving…'
              : mode === 'create'
                ? 'Create spec file'
                : 'Save changes'}
          </button>
        </div>
      </form>
    </MovableDialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}): ReactElement {
  return (
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
      <span style={{ display: 'block', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
